import { formatDate, formatUah, type OccupancyStatus } from '@hr/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  FileSpreadsheetIcon,
  NetworkIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { MoneyInput } from '@/components/money-input';
import { EmptyState } from '@/components/page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePreview } from '@/features/preview/use-preview';
import { OccupancyBadge } from '@/features/registry/occupancy-badge';
import { ProjectFormSheet } from '@/features/registry/project-form-sheet';
import { PROJECT_STATUS_STYLE } from '@/features/registry/status';
import { StaffPositionSheet } from '@/features/registry/staff-position-sheet';
import { StaffPositionsTable } from '@/features/registry/staff-positions-table';
import { PROJECT_STATUS_LABELS, uk } from '@/i18n/uk';
import { isHr } from '@/lib/roles';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { errorMessage, trpc } from '@/lib/trpc';
import type { ProjectDetails } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  tab: z.enum(['staff', 'budget']).optional().catch(undefined),
  edit: z.boolean().optional().catch(undefined),
  sp: z
    .union([z.uuid(), z.literal('new')])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_app/projects/$projectId')({
  validateSearch: searchSchema,
  component: ProjectPage,
});

function ProjectPage() {
  const openPreview = usePreview();
  const t = uk.registry;
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const project = useQuery(trpc.projects.get.queryOptions(projectId));
  const state = tableState(search, { sort: 'position', dir: 'asc' });
  const staff = useQuery({
    ...trpc.staffPositions.list.queryOptions({
      projectId,
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
    }),
    placeholderData: keepPreviousData,
  });

  if (project.isPending) return <Skeleton className="h-64" />;
  if (project.isError) return <EmptyState title={errorMessage(project.error)} />;
  const p = project.data;
  const remaining = p.budget - p.allocated;

  return (
    <>
      <div className="mb-4 space-y-3">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" /> {t.projects}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight">
              {p.code}
              <Badge variant="outline" className={PROJECT_STATUS_STYLE[p.status]}>
                {PROJECT_STATUS_LABELS[p.status]}
              </Badge>
            </h1>
            <p className="text-muted-foreground">{p.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {[
                p.donorName && `${t.donor}: ${p.donorName}`,
                `${formatDate(p.startsAt)} – ${formatDate(p.endsAt) || '…'}`,
                p.managerName && `${t.manager}: ${p.managerName}`,
              ]
                .filter(Boolean)
                .join('   ·   ')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => openPreview({ url: `/api/projects/${p.id}/staffing.xlsx` })}>
              <FileSpreadsheetIcon /> {t.exportStaffing}
            </Button>
            {canWrite && (
              <Button variant="outline" onClick={() => setSearch({ edit: true })}>
                <PencilIcon /> {uk.common.edit}
              </Button>
            )}
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t.budget} value={formatUah(p.budget)} />
          <Stat label={t.allocated} value={formatUah(p.allocated)} />
          <Stat label={t.remaining} value={formatUah(remaining)} danger={remaining < 0} />
          <Stat label={`${t.staffCount} · ${t.fteTotal}`} value={`${p.staffCount} · ${p.fteTotal}`}>
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(p.occupancy).map(([status, n]) => (
                <span key={status} className="inline-flex items-center gap-1 text-xs">
                  <OccupancyBadge status={status as OccupancyStatus} /> {n}
                </span>
              ))}
            </div>
          </Stat>
        </dl>
      </div>

      <Tabs
        value={search.tab ?? 'staff'}
        onValueChange={(v) => setSearch({ tab: v === 'staff' ? undefined : (v as 'budget') })}
      >
        <TabsList>
          <TabsTrigger value="staff">{t.tabStaff}</TabsTrigger>
          <TabsTrigger value="budget">{t.tabBudget}</TabsTrigger>
        </TabsList>
        <TabsContent value="staff" className="space-y-3 pt-2">
          {canWrite && (
            <Button onClick={() => setSearch({ sp: 'new' })}>
              <PlusIcon /> {t.newStaffPosition}
            </Button>
          )}
          <StaffPositionsTable
            data={staff.data?.items}
            total={staff.data?.total ?? 0}
            state={state}
            onStateChange={(next) => setSearch(tableStateToSearch(next))}
            isLoading={staff.isPending}
            onOpen={(id) => setSearch({ sp: id })}
            showProject={false}
            empty={
              <EmptyState icon={NetworkIcon} title={t.emptyStaffing} hint={t.emptyStaffingHint} />
            }
          />
        </TabsContent>
        <TabsContent value="budget" className="pt-2">
          <BudgetLines project={p} canWrite={canWrite} />
        </TabsContent>
      </Tabs>

      <ProjectFormSheet
        open={Boolean(search.edit)}
        onOpenChange={(o) => setSearch({ edit: o || undefined })}
        project={p}
      />
      <StaffPositionSheet
        id={search.sp}
        projectId={p.id}
        canWrite={canWrite}
        onOpenChange={(o) => !o && setSearch({ sp: undefined })}
      />
    </>
  );
}

function Stat({
  label,
  value,
  danger,
  children,
}: {
  label: string;
  value: string;
  danger?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={
          danger ? 'font-semibold text-destructive tabular-nums' : 'font-semibold tabular-nums'
        }
      >
        {value}
      </dd>
      {children}
    </div>
  );
}

function BudgetLines({ project, canWrite }: { project: ProjectDetails; canWrite: boolean }) {
  const t = uk.registry;
  const qc = useQueryClient();
  const opts = {
    onError: (e: unknown) => toast.error(errorMessage(e)),
    onSettled: () => qc.invalidateQueries(trpc.projects.pathFilter()),
  };
  const create = useMutation(trpc.projects.createBudgetLine.mutationOptions(opts));
  const update = useMutation(trpc.projects.updateBudgetLine.mutationOptions(opts));
  const remove = useMutation(trpc.projects.removeBudgetLine.mutationOptions(opts));
  const [draft, setDraft] = useState<{ code: string; name: string; amount: number | null }>({
    code: '',
    name: '',
    amount: 0,
  });
  const totalLines = project.budgetLines.reduce((s, l) => s + l.amount, 0);

  return (
    <div className="max-w-4xl space-y-3">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="w-24 px-3 py-2 text-left font-medium">{t.budgetLineCode}</th>
              <th className="px-3 py-2 text-left font-medium">{t.name}</th>
              <th className="w-44 px-3 py-2 text-right font-medium">{t.budgetLineAmount}</th>
              <th className="w-40 px-3 py-2 text-right font-medium">{t.budgetLineAllocated}</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {project.budgetLines.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="px-3 py-1.5">
                  <Input
                    defaultValue={l.code}
                    disabled={!canWrite}
                    className="h-8"
                    aria-label={t.budgetLineCode}
                    onBlur={(e) =>
                      e.target.value !== l.code && update.mutate({ id: l.id, code: e.target.value })
                    }
                  />
                </td>
                <td className="px-3 py-1.5">
                  <Input
                    defaultValue={l.name}
                    disabled={!canWrite}
                    className="h-8"
                    aria-label={t.name}
                    onBlur={(e) =>
                      e.target.value.trim() &&
                      e.target.value !== l.name &&
                      update.mutate({ id: l.id, name: e.target.value })
                    }
                  />
                </td>
                <td className="px-3 py-1.5">
                  <MoneyInput
                    value={l.amount}
                    disabled={!canWrite}
                    className="h-8"
                    onChange={(v) =>
                      v !== null && v !== l.amount && update.mutate({ id: l.id, amount: v })
                    }
                  />
                </td>
                <td
                  className={
                    l.allocated > l.amount
                      ? 'px-3 py-1.5 text-right text-destructive tabular-nums'
                      : 'px-3 py-1.5 text-right tabular-nums'
                  }
                >
                  {formatUah(l.allocated)}
                </td>
                <td className="px-1">
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label={uk.common.delete}
                      onClick={() => remove.mutate(l.id)}
                    >
                      <Trash2Icon />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {project.unassignedAllocated > 0 && (
              <tr className="border-t text-muted-foreground">
                <td />
                <td className="px-3 py-2">{t.noBudgetLine}</td>
                <td />
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatUah(project.unassignedAllocated)}
                </td>
                <td />
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t font-medium">
              <td />
              <td className="px-3 py-2">{uk.common.total(project.budgetLines.length)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatUah(totalLines)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatUah(project.allocated)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {canWrite && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.name.trim()) return;
            create.mutate(
              {
                projectId: project.id,
                code: draft.code,
                name: draft.name,
                amount: draft.amount ?? 0,
              },
              { onSuccess: () => setDraft({ code: '', name: '', amount: 0 }) },
            );
          }}
        >
          <Input
            className="w-24"
            placeholder={t.budgetLineCode}
            aria-label={t.budgetLineCode}
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
          />
          <Input
            className="w-72"
            placeholder={t.name}
            aria-label={t.name}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <MoneyInput
            className="w-44"
            value={draft.amount}
            onChange={(v) => setDraft({ ...draft, amount: v })}
          />
          <Button type="submit" disabled={!draft.name.trim() || create.isPending}>
            <PlusIcon /> {t.addBudgetLine}
          </Button>
        </form>
      )}
    </div>
  );
}
